import React from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Clock3, FileText, Sparkles, Upload } from "lucide-react";
import { useChainStore } from "../data/chainStore";
import { initialsOf } from "../components/contacts/AddContactModal";

const StageBar = ({ progress }) => (
  <div className="h-2 rounded-full bg-white/15 overflow-hidden">
    <div className="h-full rounded-full bg-gradient-to-r from-indigo-200 to-white" style={{ width: `${progress}%` }} />
  </div>
);

export default function BuyerPortalPage() {
  const { dealId } = useParams();
  const { deals, findDeal, findProperty, findContact, stages } = useChainStore();
  const deal = dealId ? findDeal(dealId) : deals[0];
  if (!deal) return null;
  const property = findProperty(deal.propertyId);
  const buyer = findContact(deal.buyerId);
  const agent = findContact(deal.agentId);
  const broker = findContact(deal.brokerId);
  const sol = findContact(deal.solicitorId);

  const completionDate = deal.targetCompletion ? new Date(deal.targetCompletion) : null;
  const daysToCompletion = completionDate ? Math.max(0, Math.ceil((completionDate.getTime() - Date.now()) / 86400000)) : 0;

  const stageIdx = stages.findIndex((s) => s.id === deal.stage);
  const tasks = [
    { id: "b1", text: "Upload Proof of Funds", done: false },
    { id: "b2", text: "Verify ID with solicitor", done: true },
    { id: "b3", text: "Sign Memorandum of Sale", done: false },
    { id: "b4", text: "Schedule survey", done: false },
  ];
  const requiredDocs = [
    { tag: "ID", label: "Photo ID", uploaded: true },
    { tag: "Proof of Funds", label: "Proof of Funds", uploaded: true },
    { tag: "Mortgage Offer", label: "Mortgage Offer", uploaded: false },
  ];

  return (
    <div className="min-h-screen pb-24 md:pb-8" style={{ background: "linear-gradient(180deg, #FAF7FF 0%, #F8FAFC 50%)" }}>
      {/* Hero */}
      <section className="px-5 md:px-8 pt-6 pb-2">
        <div className="max-w-[1100px] mx-auto rounded-3xl text-white p-6 md:p-8 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #4F46E5 60%, #4338CA 100%)" }} data-testid="buyer-welcome">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-indigo-400/30 blur-3xl pointer-events-none" />
          <p className="text-[10.5px] uppercase tracking-[0.22em] text-indigo-200 font-semibold mb-1">Buyer portal</p>
          <h1 className="font-display text-[24px] md:text-[28px] font-bold leading-tight">Hi {buyer?.name?.split(" ")[0]}, here's your purchase.</h1>
          <p className="text-[13px] text-indigo-100/85 mt-2">{property?.address}</p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_200px] gap-4 items-center">
            <div>
              <div className="flex items-center justify-between text-[11px] text-indigo-100/85 mb-1.5">
                <span className="font-semibold">{stages[stageIdx]?.label || "—"}</span>
                <span className="cf-num font-bold">{deal.progress}%</span>
              </div>
              <StageBar progress={deal.progress} />
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-3 text-center">
              <p className="text-[10px] uppercase tracking-[0.18em] text-indigo-200 font-semibold">Completion in</p>
              <p className="font-display text-[28px] font-bold cf-num leading-none mt-1">{daysToCompletion}<span className="text-[14px] text-indigo-200 ml-1">days</span></p>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1100px] mx-auto px-5 md:px-8 py-6 space-y-6">
        {/* Property */}
        <div className="rounded-3xl bg-white border border-slate-200 p-5 md:p-6 cf-shadow-0" data-testid="buyer-property-card">
          <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-5">
            <div className="aspect-[4/3] rounded-2xl bg-slate-100 overflow-hidden">{property?.thumb && <img src={property.thumb} alt="" className="w-full h-full object-cover" />}</div>
            <div>
              <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">Your purchase</p>
              <h2 className="cf-h-section text-[16px]">{property?.address?.split(",")[0]}</h2>
              <p className="text-[12.5px] text-slate-700 mt-2">{property?.address}</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-center">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Price</p>
                  <p className="text-[13px] font-bold text-indigo-700 cf-num">{deal.agreedPrice}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-center">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Beds</p>
                  <p className="text-[13px] font-bold text-slate-900 cf-num">{property?.beds}</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-center">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Postcode</p>
                  <p className="text-[13px] font-bold text-slate-900 cf-num">{property?.postcode}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cards row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ProgressCard title="Mortgage" value={broker ? "Application submitted" : "Cash buyer"} sub={broker?.company || "—"} icon={Sparkles} />
          <ProgressCard title="Solicitor" value={sol?.company || "—"} sub={sol?.name || "—"} icon={CheckCircle2} />
          <ProgressCard title="Lead negotiator" value={agent?.name || "—"} sub={agent?.company || "ChainFlow"} icon={Clock3} />
        </div>

        {/* Required documents + upload */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-3xl bg-white border border-slate-200 p-5 md:p-6" data-testid="buyer-required-docs">
            <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">Required documents</p>
            <h3 className="cf-h-section text-[16px] mb-4">What we still need</h3>
            <ul className="space-y-2">
              {requiredDocs.map((d) => (
                <li key={d.tag} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${d.uploaded ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-100 border-slate-200 text-slate-500"}`}>
                    <FileText size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-bold text-slate-900 truncate">{d.label}</p>
                    <p className="text-[10.5px] text-slate-500 truncate">{d.tag}</p>
                  </div>
                  {d.uploaded ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-emerald-50 text-emerald-700 border border-emerald-200">Uploaded</span>
                  ) : (
                    <button className="btn btn-primary !h-8 !text-[11px]" data-testid={`buyer-upload-${d.tag.replace(/\s+/g, "-")}`}><Upload size={11} /> Upload</button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-white border border-slate-200 p-5 md:p-6" data-testid="buyer-next-steps">
            <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">Next steps</p>
            <h3 className="cf-h-section text-[16px] mb-4">Outstanding tasks</h3>
            <ul className="space-y-2">
              {tasks.map((t) => (
                <li key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${t.done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white hover:border-indigo-200"}`}>
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${t.done ? "bg-emerald-500 text-white" : "border-2 border-slate-300"}`}>
                    {t.done && <CheckCircle2 size={12} />}
                  </span>
                  <p className={`text-[12.5px] flex-1 ${t.done ? "text-slate-500 line-through" : "text-slate-900 font-semibold"}`}>{t.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>

      {/* Mobile sticky actions */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 px-4 py-3 flex items-center gap-2">
        <button className="flex-1 btn btn-primary !h-11" data-testid="buyer-mobile-upload"><Upload size={13} /> Upload</button>
        <button className="flex-1 btn btn-secondary !h-11" data-testid="buyer-mobile-message"><Sparkles size={13} /> Message agent</button>
      </div>
    </div>
  );
}

const ProgressCard = ({ title, value, sub, icon: Icon }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700 flex items-center justify-center"><Icon size={13} /></div>
      <p className="cf-eyebrow">{title}</p>
    </div>
    <p className="text-[14px] font-bold text-slate-900 leading-tight">{value}</p>
    <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>
  </div>
);
